/**
 * Lightweight i18n for the Marketing section only.
 *
 * Uses a simple flat translation map with dot-separated keys.
 * Supports English (en) and Spanish (es) with English fallback.
 */

import { useCopilotStore } from '@/stores/copilotStore'

type Language = 'en' | 'es'

const strings: Record<Language, Record<string, string>> = {
  en: {
    // Copilot
    'copilot.title': 'Marketing Copilot',
    'copilot.placeholder': 'Ask about your campaigns...',
    'copilot.briefing.title': 'Marketing Summary',
    'copilot.tryAsking': 'Try asking:',
    'copilot.newConversation': 'New conversation',
    'copilot.switchLanguage': 'Switch language',
    'copilot.thinking': 'Thinking...',
    'copilot.session': 'Session',
    'copilot.tokens': 'tokens',
    'copilot.requests': 'requests',
    'copilot.request': 'request',

    // Dashboard
    'dashboard.title': 'Marketing',
    'dashboard.subtitle': 'Manage Meta ad campaigns, audiences, and conversion tracking',
    'dashboard.totalSpend': 'Total Spend',
    'dashboard.impressions': 'Impressions',
    'dashboard.clicks': 'Clicks',
    'dashboard.conversations': 'Conversations',
    'dashboard.leads': 'Leads',
    'dashboard.purchases': 'Purchases',
    'dashboard.avgCpc': 'Avg CPC',
    'dashboard.activeCampaigns': 'Active Campaigns',
    'dashboard.ofTotal': 'of {0} total',
    'dashboard.attributionFunnel': 'Attribution Funnel',
    'dashboard.ctwaPipeline': 'Click-to-WhatsApp conversion pipeline',
    'dashboard.activeCampaignsTitle': 'Active Campaigns',
    'dashboard.campaignsRunning': '{0} campaigns currently running',
    'dashboard.noActive': 'No active campaigns',
    'dashboard.optimizationRecs': 'Optimization Recommendations',
    'dashboard.actionsPending': '{0} actions pending review',
    'dashboard.analyzeOptimize': 'Analyze & Optimize',
    'dashboard.analyzing': 'Analyzing...',
    'dashboard.refresh': 'Refresh',

    // Analytics
    'analytics.performanceTrends': 'Performance Trends',
    'analytics.dailyMetrics': 'Daily metrics for the last {0} days',
    'analytics.campaignPerformance': 'Campaign Performance',
    'analytics.metricsPerCampaign': 'Metrics per campaign',
    'analytics.noData': 'No campaign data available',
    'analytics.syncFromMeta': 'Sync from Meta',

    // Campaigns
    'campaigns.new': 'New Campaign',
    'campaigns.search': 'Search campaigns...',
    'campaigns.syncing': 'Syncing...',
    'campaigns.noResults': 'No campaigns match your search.',
    'campaigns.empty': 'No campaigns yet. Create your first campaign.',

    // Creatives
    'creatives.title': 'Creatives',
    'creatives.newCreative': 'New Creative',
    'creatives.empty': 'No creatives yet. Create your first ad creative above.',
    'creatives.variation': 'Variation',
    'creatives.createVariation': 'Create Variation',

    // Audiences
    'audiences.title': 'Audiences',
    'audiences.custom': 'Custom Audience',
    'audiences.lookalike': 'Lookalike Audience',

    // Conversions
    'conversions.title': 'Conversions',
    'conversions.log': 'Conversion Log',

    // Settings
    'settings.title': 'Settings',
    'settings.connect': 'Connect Meta Ad Account',
    'settings.noAccount': 'No Meta Ad Account connected.',
    'settings.goToSettings': 'Go to the Settings tab to connect your Meta Ad Account.',

    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.approve': 'Approve',
    'common.dismiss': 'Dismiss',
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.perDay': '/day',
    'common.noDailyBudget': 'No daily budget',

    // Tabs
    'tabs.dashboard': 'Dashboard',
    'tabs.campaigns': 'Campaigns',
    'tabs.analytics': 'Analytics',
    'tabs.audiences': 'Audiences',
    'tabs.creatives': 'Creatives',
    'tabs.conversions': 'Conversions',
    'tabs.settings': 'Settings',

    // Metrics
    'metrics.ctr': 'CTR',
    'metrics.cpc': 'CPC',
    'metrics.cpl': 'CPL',
    'metrics.roas': 'ROAS',
    'metrics.spend': 'Spend',
    'metrics.revenue': 'Revenue',
    'metrics.conversionRate': 'Conversion Rate',
  },
  es: {
    // Copilot
    'copilot.title': 'Copiloto de Marketing',
    'copilot.placeholder': 'Pregunta sobre tus campañas...',
    'copilot.briefing.title': 'Resumen de Marketing',
    'copilot.tryAsking': 'Pregúntame algo:',
    'copilot.newConversation': 'Nueva conversación',
    'copilot.switchLanguage': 'Cambiar idioma',
    'copilot.thinking': 'Pensando...',
    'copilot.session': 'Sesión',
    'copilot.tokens': 'tokens',
    'copilot.requests': 'solicitudes',
    'copilot.request': 'solicitud',

    // Dashboard
    'dashboard.title': 'Marketing',
    'dashboard.subtitle': 'Administra campañas publicitarias de Meta, audiencias y seguimiento de conversiones',
    'dashboard.totalSpend': 'Gasto Total',
    'dashboard.impressions': 'Impresiones',
    'dashboard.clicks': 'Clics',
    'dashboard.conversations': 'Conversaciones',
    'dashboard.leads': 'Contactos',
    'dashboard.purchases': 'Compras',
    'dashboard.avgCpc': 'CPC Promedio',
    'dashboard.activeCampaigns': 'Campañas Activas',
    'dashboard.ofTotal': 'de {0} en total',
    'dashboard.attributionFunnel': 'Embudo de Atribución',
    'dashboard.ctwaPipeline': 'Canal de conversión clic a WhatsApp',
    'dashboard.activeCampaignsTitle': 'Campañas Activas',
    'dashboard.campaignsRunning': '{0} campañas en ejecución',
    'dashboard.noActive': 'No hay campañas activas',
    'dashboard.optimizationRecs': 'Recomendaciones de Optimización',
    'dashboard.actionsPending': '{0} acciones pendientes de revisión',
    'dashboard.analyzeOptimize': 'Analizar y Optimizar',
    'dashboard.analyzing': 'Analizando...',
    'dashboard.refresh': 'Actualizar',

    // Analytics
    'analytics.performanceTrends': 'Tendencias de Rendimiento',
    'analytics.dailyMetrics': 'Métricas diarias de los últimos {0} días',
    'analytics.campaignPerformance': 'Rendimiento de Campañas',
    'analytics.metricsPerCampaign': 'Métricas por campaña',
    'analytics.noData': 'No hay datos de campañas disponibles',
    'analytics.syncFromMeta': 'Sincronizar desde Meta',

    // Campaigns
    'campaigns.new': 'Nueva Campaña',
    'campaigns.search': 'Buscar campañas...',
    'campaigns.syncing': 'Sincronizando...',
    'campaigns.noResults': 'No se encontraron campañas.',
    'campaigns.empty': 'Aún no hay campañas. Crea tu primera campaña.',

    // Creatives
    'creatives.title': 'Creativos',
    'creatives.newCreative': 'Nuevo Creativo',
    'creatives.empty': 'Aún no hay creativos. Crea tu primer creativo arriba.',
    'creatives.variation': 'Variación',
    'creatives.createVariation': 'Crear Variación',

    // Audiences
    'audiences.title': 'Audiencias',
    'audiences.custom': 'Audiencia Personalizada',
    'audiences.lookalike': 'Audiencia Similar',

    // Conversions
    'conversions.title': 'Conversiones',
    'conversions.log': 'Registro de Conversiones',

    // Settings
    'settings.title': 'Configuración',
    'settings.connect': 'Conectar Cuenta Publicitaria de Meta',
    'settings.noAccount': 'No hay cuenta publicitaria de Meta conectada.',
    'settings.goToSettings': 'Ve a la pestaña de Configuración para conectar tu cuenta.',

    // Common
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.create': 'Crear',
    'common.approve': 'Aprobar',
    'common.dismiss': 'Descartar',
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.perDay': '/día',
    'common.noDailyBudget': 'Sin presupuesto diario',

    // Tabs
    'tabs.dashboard': 'Panel',
    'tabs.campaigns': 'Campañas',
    'tabs.analytics': 'Analíticas',
    'tabs.audiences': 'Audiencias',
    'tabs.creatives': 'Creativos',
    'tabs.conversions': 'Conversiones',
    'tabs.settings': 'Configuración',

    // Metrics
    'metrics.ctr': 'CTR',
    'metrics.cpc': 'CPC',
    'metrics.cpl': 'CPL',
    'metrics.roas': 'ROAS',
    'metrics.spend': 'Gasto',
    'metrics.revenue': 'Ingresos',
    'metrics.conversionRate': 'Tasa de Conversión',
  },
}

/**
 * Hook that returns a translation function for the marketing section.
 * Uses the copilot's language setting with English fallback.
 *
 * Supports simple interpolation with {0}, {1}, etc.
 *
 * @example
 * const t = useMarketingT()
 * t('dashboard.totalSpend') // "Total Spend" or "Gasto Total"
 * t('dashboard.ofTotal', [5]) // "of 5 total" or "de 5 en total"
 */
export function useMarketingT() {
  const language = useCopilotStore((s) => s.language) as Language

  return (key: string, args?: (string | number)[]) => {
    let value = strings[language]?.[key] ?? strings.en[key] ?? key
    if (args) {
      args.forEach((arg, i) => {
        value = value.replace(`{${i}}`, String(arg))
      })
    }
    return value
  }
}

/**
 * Get a translation without hooks (for use outside React components).
 */
export function getMarketingString(key: string, language: Language = 'en', args?: (string | number)[]): string {
  let value = strings[language]?.[key] ?? strings.en[key] ?? key
  if (args) {
    args.forEach((arg, i) => {
      value = value.replace(`{${i}}`, String(arg))
    })
  }
  return value
}
